import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Heart, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Youtube } from 'lucide-react'

export default function Footer() {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const footerLinks = {
    services: [
      { name: t('footer.allServices'), href: '/services' },
      { name: t('footer.veterinarians'), href: '/category/veterinarians' },
      { name: t('footer.therapists'), href: '/category/therapists' },
      { name: t('footer.grooming'), href: '/category/dog-groomers' },
      { name: t('footer.trainers'), href: '/category/dog-trainers' },
      { name: t('footer.boarding'), href: '/category/pet-boarding' },
      { name: t('footer.training'), href: '/category/dog-trainers' },
      { name: t('footer.trainingCourses'), href: '/category/dog-trainers' },
    ],
    adoption: [
      { name: t('footer.dogs'), href: '/adoption?species=dog' },
      { name: t('footer.cats'), href: '/adoption?species=cat' },
    ],
    content: [
      { name: t('footer.articles'), href: '/articles' },
      { name: t('footer.careTips'), href: '/articles?category=care' },
      { name: t('footer.health'), href: '/articles?category=health' },
      { name: t('footer.training'), href: '/articles?category=training' },
    ],
    company: [
      { name: t('footer.about'), href: '/about' },
      { name: t('footer.contact'), href: '/contact' },
      { name: t('footer.privacy'), href: '/privacy' },
      { name: t('footer.terms'), href: '/terms' },
    ],
  }

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-start">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <Heart className="h-8 w-8 text-primary-400" />
              <span className="mr-2 text-xl font-bold">MyPet</span>
            </div>
            <p className="text-gray-300 text-sm mb-4">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4 space-x-reverse">
              <a
                href="mailto:support@my-pety.com"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Email"
              >
                <Mail className="h-5 w-5" />
              </a>
              <a
                href="tel:+972501234567"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Phone"
              >
                <Phone className="h-5 w-5" />
              </a>
              <a
                href="https://maps.google.com"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Location"
              >
                <MapPin className="h-5 w-5" />
              </a>
            </div>
            
            {/* Social Media */}
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('footer.followUs')}</h4>
              <div className="flex space-x-4 space-x-reverse">
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="Twitter">
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                  <Youtube className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold mb-4">{t('footer.services')}</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Adoption */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold mb-4">{t('footer.adoption')}</h3>
            <ul className="space-y-2">
              {footerLinks.adoption.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Content */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold mb-4">{t('footer.content')}</h3>
            <ul className="space-y-2">
              {footerLinks.content.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm">
              © {currentYear} MyPet. {t('footer.copyright')}
            </div>
            <div className="flex space-x-6 space-x-reverse mt-4 md:mt-0">
              {footerLinks.company.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
